// Libs
import { NavLink } from 'react-router';
import invariant from 'tiny-invariant';

// RDS
import Icon from '@ramsey-design-system/icon';
import {
  EmailIcon,
  GroupIcon,
  HeartIcon,
  HelpIcon,
  HomeIcon,
  LightningIcon,
  LinkExternalIcon,
  PersonIcon
} from '@ramsey-design-system/icons';

// Components
import Header from '~fpu/shared/components/navigation/Header';
import SideNavigation from '~fpu/shared/components/navigation/SideNavigation';
import Container from '~fpu/shared/components/Container';

// Hooks
import { useOrganization } from '../Admin/hooks/useOrganization';
import { useOrganizationId } from '~fpu/modules/FpuDashboard/hooks/useOrganizationId';
import { useZendeskWidget } from '~fpu/shared/hooks/useZendeskWidget';

// Utils
import { analyticsLogger } from '~fpu/shared/utils/analytics';
import { PATHS } from '~fpu/config/constants';

// Types
import { FC } from 'react';

type OrgNavLinkProps = {
  name: string;
  isActive: boolean;
  icon: FC;
  externalLink?: boolean;
};

const orgNavigationItems = (organizationId: string) => {
  return [
    {
      path: PATHS.ORGANIZATION.ADMIN(organizationId),
      name: 'Home',
      icon: HomeIcon,
      end: true // https://reactrouter.com/en/main/components/nav-link#end
    },
    {
      path: PATHS.ORGANIZATION.CLASSES(organizationId),
      name: 'My Classes',
      icon: GroupIcon
    },
    {
      path: PATHS.ORGANIZATION.TESTIMONIALS(organizationId),
      name: 'Real Stories',
      icon: HeartIcon
    },
    {
      path: PATHS.ORGANIZATION.INSIGHTS(organizationId),
      name: 'My People',
      icon: PersonIcon
    },
    {
      path: PATHS.ORGANIZATION.PROMOTE(organizationId),
      name: 'Launch & Promote',
      icon: LightningIcon
    },
    {
      path: PATHS.ORGANIZATION.MEMBERSHIPS(organizationId),
      name: 'Send Memberships',
      icon: EmailIcon
    },
    {
      path: PATHS.ORGANIZATION.ORG_HELP_CENTER,
      name: 'Help Center',
      icon: HelpIcon,
      externalLink: true
    }
  ];
};

function NavLinkText({ name, isActive, icon, externalLink }: OrgNavLinkProps) {
  return (
    <span
      className={`flex w-full items-center rounded-md py-[12px] pl-2 ${
        isActive ? 'bg-white font-semibold text-blue-50' : 'hover:bg-gray-20'
      }`}
    >
      <Icon icon={icon} size="medium" className="mr-2" />
      {name}
      {externalLink ? (
        <Icon
          icon={LinkExternalIcon}
          size="small"
          className="ml-1.5 text-gray-50"
        />
      ) : null}
    </span>
  );
}

function OrgAdminLayout({ children }: { children: React.ReactNode }) {
  const organizationId = useOrganizationId();
  invariant(organizationId);

  useZendeskWidget('7aaa9e0e-fd40-4fb4-af28-9ae4674d3206');

  // Prefetch organization data
  useOrganization(organizationId);

  return (
    <>
      <Header withNavigation roleTag="Administrator" />
      <SideNavigation roleTag="Administrator">
        <div className="divide-gray-40 divide-y">
          <div>
            {orgNavigationItems(organizationId).map((item) =>
              item.externalLink ? (
                <NavLink
                  key={item.path}
                  target="_blank"
                  to={item.path}
                  end={item.end}
                  className="text-gray-70 flex w-full py-0.5"
                  onClick={() =>
                    analyticsLogger.trackElementClicked({
                      element: 'Org Admin Navigation',
                      label: `${item.name}`
                    })
                  }
                >
                  {({ isActive }) => (
                    <NavLinkText
                      icon={item.icon}
                      name={item.name}
                      isActive={isActive}
                      externalLink={item.externalLink}
                    />
                  )}
                </NavLink>
              ) : (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  className="text-gray-70 flex w-full py-0.5"
                  onClick={() =>
                    analyticsLogger.trackElementClicked({
                      element: 'Org Admin Navigation',
                      label: `${item.name}`
                    })
                  }
                >
                  {({ isActive }) => (
                    <NavLinkText
                      icon={item.icon}
                      name={item.name}
                      isActive={isActive}
                    />
                  )}
                </NavLink>
              )
            )}
          </div>
        </div>
      </SideNavigation>

      <div className="w-full px-2 sm:px-5 lg:pr-8 lg:pl-36">
        <Container className="mb-9 pt-4 pb-5">{children}</Container>
      </div>
    </>
  );
}

export default OrgAdminLayout;
